import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateToolMutation } from '../../../../hooks/mutations/useToolMutation';
import { useToolsQuery } from '../../../../hooks/queries/useToolsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { Tool } from '../../../../services/apiSLTP/materials/tools';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import { crudStatus, getCrudStatusInfos, getPriceTypeInfos, localStorageKeys, permissionLevels, systemConfigKeyEnum } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import UpdateToolModal from '../../../modals/admin/material/UpdateToolModal';
import DeleteModal from '../../../modals/DeleteModal';
import { formatNumber } from '../../../../utils/numbers';
import { InheritedPriceDisplay } from '../../../InheritedPriceDisplay';
import GenericAgGridTable from '../../GenericAgGridTable';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import IconButton from '../../../shared/IconButton';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface ToolsSettingsRowProps {
    withUsageCount: boolean;
}

const ToolsSettingsRow: React.FC<ToolsSettingsRowProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const { data: dataTools, isLoading: isToolsLoading, refetch: refetchTools } = useToolsQuery({ queries: { withUsage: withUsageCount } });

    const { mutate: mutateTool } = useUpdateToolMutation({
        onSuccess: () => refetchTools(),
        onError: (error) => console.error('Error updating tool:', error.message),
    });

    const sortedTools = useMemo(() => {
        return (
            dataTools?.sort((a, b) => {
                const typeA = a.toolTypeId?.displayOrder || 0;
                const typeB = b.toolTypeId?.displayOrder || 0;
                if (typeA < typeB) return -1;
                if (typeA > typeB) return 1;
                const designationA = a?.designation || '';
                const designationB = b?.designation || '';
                return designationA.localeCompare(designationB);
            }) || []
        );
    }, [dataTools]);

    const columnDefs: ColDef<Tool>[] = [
        { headerName: capitalizeFirstLetter(t('name')), field: 'designation', initialFlex: 1, minWidth: 400, colId: 'name' },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Tool>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={t('modifyTool')}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedTool(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={t('deleteTool')}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedTool(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Tool>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('baseName')),
            field: 'baseName',
            colId: 'baseName',
        },
        { headerName: capitalizeFirstLetter(t('type')), field: 'toolTypeId.designation', colId: 'type' },
        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            filter: 'agNumberColumnFilter',
            valueGetter: (params) => params.data?.price || params.data?.toolTypeId?.price,
            cellRenderer: (params: ICellRendererParams<Tool>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.toolTypeId?.price}
                        format={(v) => formatNumber(v, t('locale'))}
                    />
                );
            },
            colId: 'price',
            type: 'numericColumn',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeInfos(params.data?.priceType || params.data?.toolTypeId?.priceType))),
            cellRenderer: (params: ICellRendererParams<Tool>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.toolTypeId?.priceType}
                        format={(v) => capitalizeFirstLetter(t(getPriceTypeInfos(v)))}
                    />
                );
            },
            colId: 'pricing',
        },
        createDateColumn<Tool>('startDate', 'startDate'),
        createDateColumn<Tool>('endDate', 'endDate'),
        {
            headerName: capitalizeFirstLetter(t('model')),
            field: 'model',
            colId: 'model',
        },
        createDateTimeColumn<Tool>('createdAt', 'createdAt'),
        createDateTimeColumn<Tool>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push(
            { headerName: t('_id'), field: '_id', initialWidth: 250, colId: '_id', cellClass: 'usage-col-main', headerClass: 'usage-col-main' },
            {
                headerName: t('xTotal'),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: t('xTransfersPlanned'),
                field: 'usageCount.dailyTransfersPlanned',
                colId: 'xtransfersplanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: t('xTransfersCompleted'),
                field: 'usageCount.dailyTransfersRides',
                colId: 'xtransferscompleted',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: t('xTools'),
                field: 'usageCount.dailyTools',
                colId: 'xtools',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    return (
        <>
            <GenericAgGridTable
                rowData={sortedTools}
                columnDefs={columnDefs}
                loading={isToolsLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedTool(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={t('addTool')}
                    />
                }
                exportCsvFileName="admin-tools"
                storageKey={localStorageKeys.TABLE.ADMIN.MATERIAL.TOOLS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateToolModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    isEditMode={isEditMode}
                    tool={selectedTool}
                    refetch={refetchTools}
                />
            )}

            {showDeleteModal && selectedTool && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateTool({ toolId: selectedTool._id, data: { crudStatus: crudStatus.DELETED } });
                    }}
                    title={t('tool')}
                    itemName={selectedTool.designation}
                />
            )}
        </>
    );
};

export default ToolsSettingsRow;
